import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ShoppingCart, Heart, Minus, Plus, AlertCircle, Truck, Shield,
  Bell, BellOff, RefreshCw, ThumbsUp, QrCode, X, Star,
} from 'lucide-react';
import { productService } from '@/services/product.service';
import { cartService } from '@/services/cart.service';
import api from '@/services/api';
import { Product, Review } from '@/types';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import { RatingStars } from '@/components/common/RatingStars';
import { ProductCard } from '@/components/product/ProductCard';
import { PrescriptionUploader } from '@/components/common/PrescriptionUploader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/common/Skeleton';
import { useCartStore } from '@/store/cartStore';
import { useWishlistStore } from '@/store/wishlistStore';
import { useAuthStore } from '@/store/authStore';
import { formatCurrency, getDiscountPercent, formatDate } from '@/utils';
import toast from '@/hooks/useToast';

// Lazy-load QRCode to avoid bundle bloat if not installed yet
let QRCodeComponent: React.ComponentType<{ value: string; size: number; bgColor: string; fgColor: string }> | null = null;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  QRCodeComponent = require('qrcode.react').QRCodeSVG;
} catch { /* qrcode.react not installed */ }

export const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<(Product & { related: Product[] }) | null>(null);
  const [reviews, setReviews] = useState<(Review & { helpfulCount?: number })[]>([]);
  const [avgRating, setAvgRating] = useState(0);
  const [breakdown, setBreakdown] = useState<{ star: number; count: number }[]>([]);
  const [recommendations, setRecommendations] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [selectedImg, setSelectedImg] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [addingToCart, setAddingToCart] = useState(false);
  const [prescriptionId, setPrescriptionId] = useState<string | null>(null);
  const [refillSet, setRefillSet] = useState(false);
  const [settingRefill, setSettingRefill] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [generics, setGenerics] = useState<Product[]>([]);
  const [showQR, setShowQR] = useState(false);
  // Review form state
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewBody, setReviewBody] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [helpfulVoted, setHelpfulVoted] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('helpful_votes') || '[]')); } catch { return new Set(); }
  });

  const { setCart } = useCartStore();
  const { isInWishlist, addItem: addWish, removeItem: removeWish } = useWishlistStore();
  const { isAuthenticated } = useAuthStore();

  const fetchReviews = useCallback((productSlug: string) => {
    api.get(`/reviews/products/${productSlug}`).then(r => {
      setReviews(r.data.data.reviews || []);
      setAvgRating(r.data.data.avgRating || 0);
      setBreakdown(r.data.data.breakdown || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([
      productService.getBySlug(slug),
      api.get(`/reviews/products/${slug}`).catch(() => null),
      api.get(`/products/${slug}/generics`).catch(() => null),
      api.get(`/products/${slug}/recommendations`).catch(() => null),
    ]).then(([pRes, rRes, gRes, recRes]) => {
      setProduct(pRes.data.data);
      if (rRes) {
        setReviews(rRes.data.data.reviews || []);
        setAvgRating(rRes.data.data.avgRating || 0);
        setBreakdown(rRes.data.data.breakdown || []);
      }
      if (gRes) setGenerics(gRes.data.data || []);
      if (recRes) setRecommendations(recRes.data.data || []);
    }).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!isAuthenticated || !product) return;
    Promise.all([
      api.get('/refill-alerts').catch(() => null),
      api.get('/subscriptions').catch(() => null),
    ]).then(([alertRes, subRes]) => {
      if (alertRes) setRefillSet((alertRes.data.data || []).some((a: { productId: string }) => a.productId === product.id));
      if (subRes) setSubscribed((subRes.data.data || []).some((s: { productId: string; isActive: boolean }) => s.productId === product.id && s.isActive));
    });
  }, [product, isAuthenticated]);

  const handleSubscribe = async () => {
    if (!isAuthenticated) { toast.info('Please login to subscribe'); return; }
    if (!product) return;
    setSubscribing(true);
    try {
      if (subscribed) {
        await api.delete(`/subscriptions/by-product/${product.id}`);
        setSubscribed(false);
        toast.info('Subscription cancelled');
      } else {
        await api.post('/subscriptions', { productId: product.id, quantity: qty, intervalDays: 30 });
        setSubscribed(true);
        toast.success('Subscribed!', 'We\'ll add this to your cart every 30 days.');
      }
    } catch { toast.error('Could not update subscription'); }
    finally { setSubscribing(false); }
  };

  const handleRefillAlert = async () => {
    if (!isAuthenticated) { toast.info('Please login to set reminders'); return; }
    if (!product) return;
    setSettingRefill(true);
    try {
      if (refillSet) {
        await api.delete(`/refill-alerts/${product.id}`);
        setRefillSet(false);
        toast.info('Refill reminder removed');
      } else {
        await api.post('/refill-alerts', { productId: product.id, alertDays: 25 });
        setRefillSet(true);
        toast.success('Refill reminder set!', 'We\'ll remind you in 25 days to reorder.');
      }
    } catch { toast.error('Could not update reminder'); }
    finally { setSettingRefill(false); }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    if (product.requiresPrescription && !prescriptionId) {
      toast.info('Prescription Required', 'Please upload your prescription first.');
      return;
    }
    setAddingToCart(true);
    try {
      const res = await cartService.addItem(product.id, qty);
      setCart(res.data.data);
      toast.success('Added to cart!');
    } catch { toast.error('Could not add to cart'); }
    finally { setAddingToCart(false); }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) { toast.info('Please login to use wishlist.'); return; }
    if (!product) return;
    if (isInWishlist(product.id)) {
      await api.delete(`/wishlist/items/${product.id}`);
      removeWish(product.id);
      toast.info('Removed from wishlist');
    } else {
      const res = await api.post('/wishlist/items', { productId: product.id });
      addWish(res.data.data);
      toast.success('Added to wishlist!');
    }
  };

  const handleSubmitReview = async () => {
    if (!isAuthenticated) { toast.info('Please login to write a review'); return; }
    if (!product) return;
    setSubmittingReview(true);
    try {
      await api.post(`/reviews/products/${product.id}`, {
        rating: reviewRating,
        title: reviewTitle || undefined,
        body: reviewBody || undefined,
      });
      toast.success('Review submitted!');
      setReviewTitle('');
      setReviewBody('');
      setReviewRating(5);
      fetchReviews(slug!);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Could not submit review');
    } finally { setSubmittingReview(false); }
  };

  const handleHelpful = async (reviewId: string) => {
    if (helpfulVoted.has(reviewId)) return;
    try {
      const res = await api.post(`/reviews/${reviewId}/helpful`);
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, helpfulCount: res.data.data.helpfulCount } : r));
      const next = new Set(helpfulVoted).add(reviewId);
      setHelpfulVoted(next);
      localStorage.setItem('helpful_votes', JSON.stringify([...next]));
    } catch { /* ignore */ }
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-8 grid md:grid-cols-2 gap-10">
      <Skeleton className="h-96 rounded-2xl" />
      <div className="space-y-4"><Skeleton className="h-8 w-3/4" /><Skeleton className="h-5 w-1/2" /><Skeleton className="h-10 w-1/3" /></div>
    </div>
  );

  if (!product) return <div className="text-center py-20 text-gray-500">Product not found</div>;

  const discountPct = product.discountedPrice ? getDiscountPercent(product.price, product.discountedPrice) : null;
  const inWishlist = isInWishlist(product.id);
  const productUrl = `${window.location.origin}/products/${product.slug}`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <Breadcrumb items={[
        { label: 'Home', href: '/' },
        { label: product.category?.name || 'Category', href: `/categories/${product.category?.slug}` },
        { label: product.name },
      ]} />

      <div className="grid md:grid-cols-2 gap-10 mt-6">
        {/* Image Gallery */}
        <div>
          <div className="relative bg-gray-50 rounded-2xl overflow-hidden h-80 md:h-96 mb-4">
            <img
              src={product.images[selectedImg] || '/placeholder-product.jpg'}
              alt={product.name}
              className="w-full h-full object-contain p-4"
            />
            {discountPct && (
              <Badge variant="accent" className="absolute top-4 left-4 text-sm">-{discountPct}%</Badge>
            )}
            {/* QR Code button */}
            <button
              onClick={() => setShowQR(true)}
              className="absolute bottom-3 right-3 bg-white/90 hover:bg-white p-2 rounded-xl shadow-md transition-all"
              title="Share via QR Code"
            >
              <QrCode className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setSelectedImg(i)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === selectedImg ? 'border-primary-500' : 'border-transparent'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          {product.brand && <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">{product.brand.name}</p>}
          <h1 className="font-heading font-bold text-2xl text-gray-800 mb-3">{product.name}</h1>

          <div className="flex items-center gap-3 mb-4">
            <RatingStars rating={avgRating} size="md" />
            <span className="text-sm text-gray-500">({reviews.length} reviews)</span>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl font-bold text-primary-600">{formatCurrency(Number(product.discountedPrice || product.price))}</span>
            {product.discountedPrice && (
              <span className="text-lg text-gray-400 line-through">{formatCurrency(Number(product.price))}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {product.requiresPrescription && <Badge variant="rx">Prescription Required</Badge>}
            {product.drugSchedule && <Badge variant="schedule">Schedule {product.drugSchedule}</Badge>}
            {product.dosageForm && <Badge variant="secondary">{product.dosageForm}</Badge>}
            {product.stock > 0 ? <Badge variant="success">In Stock ({product.stock})</Badge> : <Badge variant="destructive">Out of Stock</Badge>}
          </div>

          <p className="text-gray-600 text-sm leading-relaxed mb-5">{product.shortDesc}</p>

          {product.requiresPrescription && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-800">Prescription Required</p>
                  <p className="text-xs text-yellow-700 mt-0.5">Upload a valid prescription before adding to cart. Our pharmacist will verify it.</p>
                </div>
              </div>
              {!prescriptionId && (
                <div className="mt-3">
                  <PrescriptionUploader onUploaded={p => setPrescriptionId(p.id)} />
                </div>
              )}
              {prescriptionId && (
                <p className="text-xs text-success font-semibold mt-2">✓ Prescription uploaded — you can now add to cart.</p>
              )}
            </div>
          )}

          {/* Qty + Add to cart */}
          <div className="flex items-center gap-4 mb-5">
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 text-gray-600">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-semibold">{qty}</span>
              <button onClick={() => setQty(q => Math.min(product.stock, q + 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-50 text-gray-600">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button className="flex-1" loading={addingToCart} onClick={handleAddToCart} disabled={product.stock === 0}>
              <ShoppingCart className="h-4 w-4 mr-2" />
              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </Button>
            <button onClick={handleWishlist} title="Wishlist" className={`p-2.5 rounded-xl border-2 transition-all ${inWishlist ? 'border-red-200 bg-red-50 text-red-500' : 'border-gray-200 text-gray-400 hover:border-red-200 hover:text-red-500'}`}>
              <Heart className="h-5 w-5" fill={inWishlist ? 'currentColor' : 'none'} />
            </button>
            <button onClick={handleRefillAlert} disabled={settingRefill} title={refillSet ? 'Remove refill reminder' : 'Set refill reminder'} className={`p-2.5 rounded-xl border-2 transition-all ${refillSet ? 'border-primary-200 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-400 hover:border-primary-200 hover:text-primary-600'}`}>
              {refillSet ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
            </button>
            <button onClick={handleSubscribe} disabled={subscribing} title={subscribed ? 'Cancel auto-refill' : 'Subscribe (auto-refill monthly)'} className={`p-2.5 rounded-xl border-2 transition-all ${subscribed ? 'border-green-200 bg-green-50 text-green-600' : 'border-gray-200 text-gray-400 hover:border-green-200 hover:text-green-600'}`}>
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
              <Truck className="h-4 w-4 text-primary-500" />Free delivery above ₹500
            </div>
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
              <Shield className="h-4 w-4 text-primary-500" />100% genuine product
            </div>
          </div>

          {product.expiryDate && (
            <p className="text-xs text-gray-400 mt-3">Expiry: {formatDate(product.expiryDate)}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12">
        <div className="flex gap-1 border-b border-gray-200 mb-6 overflow-x-auto">
          {['description', 'usage', 'reviews'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-semibold capitalize transition-colors border-b-2 -mb-px whitespace-nowrap ${activeTab === tab ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'reviews' ? `Reviews (${reviews.length})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'description' && (
          <div className="prose prose-sm max-w-none text-gray-600">
            <p>{product.description || product.shortDesc || 'No detailed description available.'}</p>
            {product.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {product.tags.map(t => <Badge key={t} variant="secondary" className="capitalize">{t}</Badge>)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="text-sm text-gray-600 space-y-2">
            {product.dosageForm && <p><strong>Form:</strong> {product.dosageForm}</p>}
            {product.manufacturer && <p><strong>Manufacturer:</strong> {product.manufacturer}</p>}
            {product.drugSchedule && <p><strong>Drug Schedule:</strong> Schedule {product.drugSchedule} (Prescription Required)</p>}
            <p className="text-xs text-gray-400 mt-4">Please consult your physician before taking this medication. Do not self-medicate.</p>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Rating breakdown */}
            {reviews.length > 0 && (
              <div className="bg-gray-50 rounded-2xl p-5 flex flex-col sm:flex-row gap-6">
                <div className="text-center shrink-0">
                  <p className="text-5xl font-bold text-gray-800">{avgRating.toFixed(1)}</p>
                  <RatingStars rating={avgRating} size="md" className="justify-center mt-1" />
                  <p className="text-xs text-gray-400 mt-1">{reviews.length} reviews</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {breakdown.map(b => (
                    <div key={b.star} className="flex items-center gap-2 text-xs">
                      <span className="w-3 text-gray-500">{b.star}</span>
                      <Star className="h-3 w-3 text-accent fill-accent shrink-0" />
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-accent h-1.5 rounded-full transition-all"
                          style={{ width: reviews.length > 0 ? `${(b.count / reviews.length) * 100}%` : '0%' }}
                        />
                      </div>
                      <span className="text-gray-400 w-4">{b.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Write review form */}
            {isAuthenticated && (
              <div className="bg-white rounded-xl shadow-card p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-800 mb-4">Write a Review</h3>
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setReviewRating(s)}>
                      <Star className={`h-6 w-6 transition-colors ${s <= reviewRating ? 'text-accent fill-accent' : 'text-gray-300'}`} />
                    </button>
                  ))}
                </div>
                <input
                  value={reviewTitle}
                  onChange={e => setReviewTitle(e.target.value)}
                  placeholder="Review title (optional)"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <textarea
                  value={reviewBody}
                  onChange={e => setReviewBody(e.target.value)}
                  placeholder="Share your experience with this product..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                />
                <Button size="sm" loading={submittingReview} onClick={handleSubmitReview}>
                  Submit Review
                </Button>
              </div>
            )}

            {/* Review list */}
            {reviews.length === 0 ? (
              <p className="text-gray-500 text-sm">No reviews yet. Be the first to review!</p>
            ) : (
              reviews.map(r => (
                <div key={r.id} className="bg-white rounded-xl shadow-card p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-sm">
                      {r.user?.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{r.user?.name}</p>
                      <p className="text-xs text-gray-400">{formatDate(r.createdAt)}</p>
                    </div>
                    <RatingStars rating={r.rating} size="sm" className="ml-auto" />
                    {r.isVerified && (
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-semibold">Verified</span>
                    )}
                  </div>
                  {r.title && <p className="font-medium text-gray-700 mb-1">{r.title}</p>}
                  {r.body && <p className="text-sm text-gray-600 mb-3">{r.body}</p>}
                  {r.images && r.images.length > 0 && (
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {r.images.map((img, i) => (
                        <img key={i} src={img} alt="Review" className="w-16 h-16 rounded-lg object-cover border border-gray-100" />
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => handleHelpful(r.id)}
                    disabled={helpfulVoted.has(r.id)}
                    className={`flex items-center gap-1.5 text-xs transition-colors ${helpfulVoted.has(r.id) ? 'text-primary-600 font-semibold' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                    Helpful {r.helpfulCount ? `(${r.helpfulCount})` : ''}
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Generic Alternatives */}
      {generics.length > 0 && (
        <section className="mt-8 bg-green-50 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">💊</span>
            <h2 className="font-heading font-bold text-lg text-gray-800">Generic Alternatives Available</h2>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Save money</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {generics.map(g => {
              const savings = (product?.price || 0) - Number(g.discountedPrice || g.price);
              return (
                <Link key={g.id} to={`/products/${g.slug}`} className="bg-white rounded-xl p-4 hover:shadow-md transition-all">
                  <p className="font-semibold text-gray-800 text-sm mb-1">{g.name}</p>
                  <p className="text-xs text-gray-400 mb-2">{g.brand?.name || 'Generic'}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary-600">{formatCurrency(Number(g.discountedPrice || g.price))}</span>
                    {savings > 0 && <span className="text-xs text-green-600 font-semibold">Save {formatCurrency(savings)}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Smart Recommendations */}
      {recommendations.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl text-gray-800 mb-5">🛒 Customers Also Bought</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {recommendations.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* Related Products */}
      {product.related && product.related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading font-bold text-xl text-gray-800 mb-5">Related Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {product.related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-xs w-full text-center shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-gray-800">Quick Reorder QR</h3>
              <button onClick={() => setShowQR(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            {QRCodeComponent ? (
              <div className="flex justify-center mb-4">
                <QRCodeComponent value={productUrl} size={200} bgColor="#ffffff" fgColor="#000000" />
              </div>
            ) : (
              <div className="h-48 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
                <p className="text-sm text-gray-400">QR unavailable</p>
              </div>
            )}
            <p className="text-xs text-gray-500 mb-3">Scan to open this product on any device</p>
            <p className="text-xs font-semibold text-gray-700 break-all">{product.name}</p>
          </div>
        </div>
      )}
    </div>
  );
};
